/**
 * KAPE 2.0 · Course Assets Editor
 *
 * Rich-text authoring surface for every course-level asset. Replaces
 * JSON editing with independently editable sections that each carry:
 *   · TipTap rich editor
 *   · AI regenerate menu (regenerate / improve / shorten / expand /
 *     Dr. Christy voice / beginners / professionals)
 *   · Version history + restore
 *   · Save (per section, never regenerates the others)
 *
 * Route: rendered inside `KapeCourseEditor` as the "Course Assets" tab.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";
import RichEditor from "@/components/editors/RichEditor";

const SECTIONS = [
  { key: "welcome_lecture_md",     label: "Welcome Lecture",         hint: "Dr. Christy's personal opening for the course." },
  { key: "course_introduction_md", label: "Course Introduction",     hint: "How to use this course + what to expect." },
  { key: "marketing_copy_md",      label: "Marketing Description",   hint: "Catalog copy — lead with the transformation." },
  { key: "learning_outcomes_md",   label: "Learning Outcomes",       hint: "Measurable outcomes phrased to the student." },
  { key: "student_resources_md",   label: "Student Resources",       hint: "Books, articles, links, KCIS references." },
  { key: "certificate_text",       label: "Certificate Language",    hint: "Text printed on the graduation certificate." },
  { key: "final_project_md",       label: "Final Practical Project", hint: "The culminating deliverable — real professional work." },
  { key: "instructor_notes_md",    label: "Instructor Notes",        hint: "Notes for delivering this course to a live cohort." },
  { key: "closing_message_md",     label: "Course Closing Message",  hint: "Shown to students when they finish the last lesson." },
];

const MODES = [
  { v: "regenerate",   l: "Regenerate" },
  { v: "improve",      l: "Improve" },
  { v: "shorten",      l: "Shorten" },
  { v: "expand",       l: "Expand" },
  { v: "voice",        l: "In Dr. Christy's voice" },
  { v: "beginners",    l: "For beginners" },
  { v: "professionals",l: "For professionals" },
];

export default function CourseAssetsEditor({ course, reload }) {
  const [assets, setAssets] = useState(course.course_assets || {});

  useEffect(() => { setAssets(course.course_assets || {}); }, [course.course_assets]);

  return (
    <div className="space-y-5" data-testid="course-assets-editor">
      <div className="rounded-2xl p-5 border"
           style={{ borderColor: PAL.border, background: PAL.panel }}>
        <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
          KAPE 2.0 · Course Assets
        </div>
        <h2 className="text-lg font-semibold mt-1">Author every course-level asset</h2>
        <p className="text-xs mt-1" style={{ color: PAL.muted }}>
          Each section is independently editable. Saving one section will not regenerate the others.
          Every regenerate action snapshots the previous version so you can restore it any time.
        </p>
      </div>

      {SECTIONS.map((s) => (
        <AssetSection
          key={s.key}
          section={s}
          courseId={course.id}
          value={assets[s.key] || ""}
          onSaved={(text) => setAssets((a) => ({ ...a, [s.key]: text }))}
          reload={reload}
        />
      ))}
    </div>
  );
}


function AssetSection({ section, courseId, value, onSaved, reload }) {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => { setDraft(value); setDirty(false); }, [value]);

  const onChange = (v) => { setDraft(v); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/kcis/admin/kape/courses/${courseId}/course-assets`,
                       { [section.key]: draft });
      onSaved(draft);
      setDirty(false);
      toast.success(`${section.label} saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runRegenerate = async (mode) => {
    setBusy(mode);
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/courses/${courseId}/course-assets/${section.key}/regenerate`,
        { mode }
      );
      setDraft(data.text);
      onSaved(data.text);
      setDirty(false);
      toast.success(`${section.label} · ${mode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setBusy(""); }
  };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/kcis/admin/kape/courses/${courseId}/course-assets/${section.key}/history`
      );
      setHistory(data.items || []);
    } catch { setHistory([]); }
  }, [courseId, section.key]);

  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore this version of "${section.label}"? Current text will be snapshotted first.`)) return;
    try {
      const { data } = await api.get(`/kcis/admin/kape/versions/${v.id}`);
      const restoredText = data?.version?.snapshot?.text ?? data?.snapshot?.text ?? "";
      // Save into the course_assets directly.
      await api.patch(`/kcis/admin/kape/courses/${courseId}/course-assets`,
                       { [section.key]: restoredText });
      setDraft(restoredText);
      onSaved(restoredText);
      toast.success("Restored");
      await reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div className="rounded-2xl p-5 border"
         style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid={`asset-section-${section.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold" data-testid={`asset-title-${section.key}`}>
            {section.label}
          </div>
          <div className="text-xs mt-0.5" style={{ color: PAL.muted }}>{section.hint}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RegenerateMenu section={section} runRegenerate={runRegenerate} busy={busy} />
          <button onClick={() => setPreview((p) => !p)}
                  className="text-xs rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid={`asset-preview-${section.key}`}>
            {preview ? "Edit" : "Preview"}
          </button>
          <button onClick={loadHistory}
                  className="text-xs rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: PAL.muted }}
                  data-testid={`asset-history-${section.key}`}>
            History
          </button>
          <button onClick={save} disabled={!dirty || saving}
                  className="text-xs rounded-full px-3 py-1 disabled:opacity-40"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid={`asset-save-${section.key}`}>
            {saving ? "Saving…" : "Save section"}
          </button>
        </div>
      </div>

      <div className="mt-3">
        {preview ? (
          <div className="rounded-lg p-4 text-sm whitespace-pre-wrap"
               style={{ background: "#0F0B14", border: `1px solid ${PAL.border}`, color: PAL.text }}
               data-testid={`asset-preview-body-${section.key}`}>
            {draft || <span style={{ color: PAL.muted }}>(empty)</span>}
          </div>
        ) : (
          <RichEditor value={draft} onChange={onChange}
                       placeholder={`Write the ${section.label.toLowerCase()}…`}
                       testid={`asset-editor-${section.key}`} />
        )}
      </div>

      {history !== null && (
        <div className="mt-3 rounded-lg p-3 text-xs"
             style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
             data-testid={`asset-history-list-${section.key}`}>
          <div className="uppercase tracking-widest mb-2" style={{ color: PAL.muted }}>
            Version history · {history.length}
          </div>
          {history.length === 0 ? (
            <div style={{ color: PAL.muted }}>No prior versions yet.</div>
          ) : (
            <ul className="space-y-1">
              {history.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-2">
                  <span>
                    <span style={{ color: PAL.accent }}>v{v.version_number}</span>{" "}
                    · {v.source} · {v.summary} · {v.edited_by || "system"} · {v.created_at?.slice(0, 16)}
                  </span>
                  <button onClick={() => restoreVersion(v)}
                          className="text-[11px] px-2 py-0.5 rounded-full border"
                          style={{ borderColor: PAL.border, color: PAL.accent }}
                          data-testid={`asset-restore-${section.key}-${v.id}`}>
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


function RegenerateMenu({ section, runRegenerate, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)}
              disabled={!!busy}
              className="text-xs rounded-full px-3 py-1 disabled:opacity-40"
              style={{ background: "#7C3AED", color: "white" }}
              data-testid={`asset-regen-menu-${section.key}`}>
        {busy ? `${busy}…` : "AI Regenerate ▾"}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 rounded-lg overflow-hidden shadow-lg"
             style={{ background: "#1B1523", border: `1px solid ${PAL.border}`, minWidth: 200 }}
             data-testid={`asset-regen-list-${section.key}`}
             onMouseLeave={() => setOpen(false)}>
          {MODES.map((m) => (
            <button key={m.v}
                    onClick={() => { setOpen(false); runRegenerate(m.v); }}
                    className="block w-full text-left text-xs px-3 py-2 hover:bg-purple-900/40"
                    style={{ color: PAL.text }}
                    data-testid={`asset-regen-${section.key}-${m.v}`}>
              {m.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
